/**
 * This module provides the form handler for the Power-Meter-Settings of a Storage device.
 *
 * It uses a work in progress implementation of the Formik form handler.
 *
 * The goal of the WIP here is:
 * - to demonstrate how Formik can be integrated
 * - to demonstrate how Formik behaves
 * - test how existing customized component react with Formik
 *
 * With the results we can:
 * - decide if we continue working with it
 * - decide how to model a generic Form component
 * - decide how to model Input component which require the Form connectors
 */

import React, { useMemo } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { PowerMeterModel } from 'src/Libs/power-meters'
import TextField from 'src/Libs/inputs/formik/TextField'
import SettingsBox from 'src/Elements/Data/SettingsBox'
import PhaseFieldSet from 'src/Libs/power-meters/components/settings/fields/PhaseFieldSet'
import { SettingsForm } from 'src/Elements/SystemSettings/forms'
import { PMType } from 'src/Libs/power-meters/types'
import { SystemSettingsRestrictionVariant, SystemSettingsRestrictionVariants } from 'src/Libs/system-settings/types'
import { SETTING_RESTRICTED_TO_CLIENT_TRANSLATION_KEY } from 'src/Libs/farm/constants'
import { OptionalTooltipWrapper } from '@e3dc-react/shell/Elements/Tooltip'
import { useSoftwareVersion } from 'src/Libs/home-power-station'
import { canChangePowermeterName } from '../../utils'

interface Props {
  model: PowerMeterModel
  variant?: SystemSettingsRestrictionVariant
  disabled?: boolean
}

/**
 * Form which allows to set various settings for a power meter.
 */
const PowerMeterSettingsForm: React.FC<Props> = ({ model, variant = SystemSettingsRestrictionVariants.Default, disabled = false }) => {
  const { t } = useCustomTranslation()
  const isChangeable = useMemo(() => model.conditionType === PMType.PM_TYPE_ROOT, [model.conditionType])
  const applyServerRestriction = variant === SystemSettingsRestrictionVariants.Server
  const clientRestrictionsTooltipText = applyServerRestriction ? SETTING_RESTRICTED_TO_CLIENT_TRANSLATION_KEY : undefined
  const { softwareVersion } = useSoftwareVersion()
  const tooltipTextKey =
    model.conditionType === PMType.PM_TYPE_ROOT
      ? 'systemSettings.powermeter.settings.tooltip-no-name-change'
      : 'systemSettings.powermeter.settings.tooltip-no-name-change-temp'

  const disableNameChange = !canChangePowermeterName(model.conditionType, softwareVersion ?? '')

  return (
    <SettingsForm model={model}>
      <OptionalTooltipWrapper hasTooltip={disableNameChange} titleKey={tooltipTextKey}>
        <SettingsBox
          title={t('systemSettings.powerMeter.settings.deviceName')}
          disabled={disabled}
          suffix={<TextField name="deviceName" disabled={disableNameChange} label={t('systemSettings.powerMeter.settings.deviceName')} />}
        />
      </OptionalTooltipWrapper>
      <PhaseFieldSet
        label={t('systemSettings.powerMeter.settings.phaseElimination')}
        name="phaseElimination"
        disabled={!isChangeable || disabled || applyServerRestriction}
        tooltipText={clientRestrictionsTooltipText}
      />
      {isChangeable && (
        <PhaseFieldSet
          label={t('systemSettings.powerMeter.settings.balancedPhases')}
          name="balancedPhases"
          disabled={disabled || applyServerRestriction}
          tooltipText={clientRestrictionsTooltipText}
        />
      )}
    </SettingsForm>
  )
}

export default PowerMeterSettingsForm
